import { useState, useCallback, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChipEditor.module.css';

export interface ChipEditorProps {
  values: string[];
  onChange: (values: string[]) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
}

export function ChipEditor({
  values,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  disabled = false,
}: ChipEditorProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const addValue = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (trimmed && !values.includes(trimmed)) {
        onChange([...values, trimmed]);
      }
      setInputValue('');
    },
    [values, onChange]
  );

  const removeValue = useCallback(
    (valueToRemove: string) => {
      onChange(values.filter((v) => v !== valueToRemove));
    },
    [values, onChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addValue(inputValue);
      } else if (e.key === 'Backspace' && !inputValue && values.length > 0) {
        removeValue(values[values.length - 1]);
      }
    },
    [inputValue, values, addValue, removeValue]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      if (pastedText.includes('\n') || pastedText.includes(',')) {
        e.preventDefault();
        const newValues = pastedText
          .split(/[\n,]/)
          .map((v) => v.trim())
          .filter((v) => v && !values.includes(v));
        if (newValues.length > 0) {
          onChange([...values, ...newValues]);
        }
      }
    },
    [values, onChange]
  );

  return (
    <div className={styles.wrapper}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={`${styles.container} ${error ? styles.error : ''} ${disabled ? styles.disabled : ''}`}>
        <div className={styles.chips}>
          {values.map((value) => (
            <span key={value} className={styles.chip}>
              <span className={styles.chipText}>{value}</span>
              {!disabled && (
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => removeValue(value)}
                  aria-label={`Remove ${value}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {!disabled && (
            <input
              type="text"
              className={styles.input}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={() => inputValue && addValue(inputValue)}
              placeholder={values.length === 0 ? placeholder || t('form.validValuesPlaceholder') : ''}
            />
          )}
        </div>
      </div>
      {error && <p className={styles.errorText}>{error}</p>}
      {helperText && !error && <p className={styles.helperText}>{helperText}</p>}
      <p className={styles.hint}>{t('form.validValuesHelp')}</p>
    </div>
  );
}
